import React from "react";
import { Link } from "react-router-dom";
import { Building2, Target, Compass, ArrowRight, User } from "lucide-react";
import SplitPageHero from "../components/SplitPageHero";
import Reveal from "../components/Reveal";
import { Button } from "../components/Button";
import { COMPANY, WHY_CHOOSE, IMAGES } from "../data";

export default function About() {
  return (
    <div data-testid="about-page">
      <SplitPageHero
        eyebrow="About MRL Transports"
        titleGreen="Who We Are."
        titleOrange="Built for Wind."
        subtitle="A family-led specialised transportation company, purpose-built to move the equipment powering India's clean-energy future."
        imageWebp="/hero-about.webp"
        imagePng="/hero-about.png"
        imageAlt="MRL Transports fleet lineup at operations yard"
        badgeLabel="Est. 2007"
        badgeSub="Based in Namakkal"
      />

      {/* STORY */}
      <section className="py-24 md:py-32 bg-white" data-testid="about-story">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={IMAGES.about}
                  alt="Wind logistics operations"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute -bottom-1 -right-1 bg-brand-orange text-white p-6">
                  <p className="font-heading text-6xl leading-none">{COMPANY.founded}</p>
                  <p className="font-mono text-[11px] uppercase tracking-widest mt-1">Founded</p>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal><p className="section-tag">Our Story</p></Reveal>
            <Reveal delay={100}>
              <h2 className="font-heading text-4xl md:text-5xl leading-[0.95] tracking-tightest mt-6 text-brand-ink">
                Moving India's wind energy — one turbine at a time.
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <div className="mt-8 space-y-6 font-body text-lg text-zinc-700 leading-relaxed">
                <p>
                  Behind every wind energy project is a complex logistics operation that ensures critical components
                  reach their destination safely and on schedule. MRL Transports specialises in the transportation of
                  wind turbine components, inter-carting operations and over-dimensional cargo, supporting renewable
                  energy projects across India with specialised equipment and experienced execution.
                </p>
                <p>
                  Founded in {COMPANY.founded} and based in {COMPANY.city}, Tamil Nadu, MRL Transports is a
                  family-led company built on a foundation of reliability, commitment and operational excellence.
                  The company is led by <span className="font-semibold text-brand-green">{COMPANY.leader.name}</span>,
                  who brings over 25 years of hands-on experience in specialised logistics and India's wind energy
                  sector. Under his leadership, MRL Transports continues to support the growth of India's renewable
                  energy industry — building lasting relationships with leading industry players while executing
                  projects across diverse geographies and operating conditions throughout the country.
                </p>
              </div>
            </Reveal>

            {/* Leadership card */}
            <Reveal delay={250}>
              <div className="mt-10 border-l-4 border-brand-orange bg-brand-smoke p-6 md:p-8" data-testid="about-leader-card">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 shrink-0 flex items-center justify-center bg-brand-green">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-brand-orange">Leadership</p>
                    <p className="font-heading text-2xl md:text-3xl mt-2 tracking-tight text-brand-ink">
                      {COMPANY.leader.name}
                    </p>
                    <p className="font-body text-sm text-zinc-600 mt-1">{COMPANY.leader.title}</p>
                    <p className="font-body text-sm text-zinc-700 mt-3 leading-relaxed">
                      Over 25 years of experience in specialised logistics and India's wind energy sector.
                      Personally involved in project planning and site execution to guarantee safety and reliability
                      on every movement.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={300}>
              <div className="mt-10 grid grid-cols-2 gap-6 border-t border-zinc-200 pt-8 max-w-lg">
                <div>
                  <p className="font-heading text-5xl text-brand-green leading-none">25+</p>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mt-2">Years experience</p>
                </div>
                <div>
                  <p className="font-heading text-5xl text-brand-orange leading-none">10+</p>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mt-2">Blue-chip clients</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VISION MISSION */}
      <section className="py-24 md:py-32 bg-brand-smoke" data-testid="about-vision-mission">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Reveal>
            <div className="bg-white border-t-4 border-brand-green p-10 md:p-14 h-full" data-testid="about-vision-card">
              <div className="flex items-center gap-3">
                <Compass className="w-6 h-6 text-brand-green" />
                <p className="font-mono text-xs uppercase tracking-widest text-brand-green">Vision</p>
              </div>
              <h3 className="font-heading text-3xl md:text-4xl leading-tight tracking-tight mt-6 text-brand-ink">
                To be India's most trusted specialised transportation partner — recognised for safety, reliability and operational excellence.
              </h3>
              <p className="font-body text-base text-zinc-600 mt-6 leading-relaxed">
                Delivering every project on time, without incident, and to the complete satisfaction of every client
                we serve — while enabling the growth of India's renewable energy sector.
              </p>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="bg-white border-t-4 border-brand-orange p-10 md:p-14 h-full" data-testid="about-mission-card">
              <div className="flex items-center gap-3">
                <Target className="w-6 h-6 text-brand-orange" />
                <p className="font-mono text-xs uppercase tracking-widest text-brand-orange">Mission</p>
              </div>
              <h3 className="font-heading text-3xl md:text-4xl leading-tight tracking-tight mt-6 text-brand-ink">
                Safety-first execution. Reliable delivery. Complete client satisfaction — on every project, every time.
              </h3>
              <p className="font-body text-base text-zinc-600 mt-6 leading-relaxed">
                Through specialised equipment, disciplined operational excellence, trained crews and hands-on leadership,
                we ensure every consignment is moved safely, reliably and to the highest professional standard.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY CHOOSE US (full list on about) */}
      <section className="py-24 md:py-32 bg-white" data-testid="about-why">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-6">
              <Reveal><p className="section-tag">Why Work With Us</p></Reveal>
              <Reveal delay={100}>
                <h2 className="font-heading text-4xl md:text-6xl leading-[0.95] tracking-tightest mt-6 text-brand-ink">
                  Six reasons India's<br />wind industry trusts MRL.
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-zinc-200">
            {WHY_CHOOSE.map((w, i) => (
              <Reveal key={w.title} delay={i * 80}>
                <div className="p-8 md:p-10 border-r border-b border-zinc-200 min-h-[240px]" data-testid={`about-why-card-${i}`}>
                  <p className="font-mono text-xs text-brand-orange">0{i + 1}</p>
                  <h3 className="font-heading text-2xl mt-4 tracking-tight text-brand-ink">{w.title}</h3>
                  <p className="font-body text-sm mt-3 leading-relaxed text-zinc-600">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HQ CTA */}
      <section className="py-24 bg-brand-ink text-white" data-testid="about-hq">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3">
              <Building2 className="w-6 h-6 text-brand-orange" />
              <p className="font-mono text-xs uppercase tracking-widest text-brand-orange">Based in Namakkal</p>
            </div>
            <h2 className="font-heading text-4xl md:text-6xl leading-[0.95] tracking-tightest mt-6">
              {COMPANY.city}, {COMPANY.region}.<br />The transport capital of India.
            </h2>
            <p className="font-body text-lg text-zinc-300 mt-6 max-w-2xl">
              Operating from Namakkal — India's heavy-transport hub — gives us unmatched access to skilled crews,
              proven vendors and rapid deployment across every corner of the country.
            </p>
          </div>
          <div className="lg:col-span-4">
            <Link to="/contact" data-testid="about-visit-cta">
              <Button variant="secondary" size="lg" className="w-full">Visit Us <ArrowRight className="w-4 h-4" /></Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
